import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartData, setCartData] = useState({ items: [], total_items: 0, total_price: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const { user, token } = useAuth();
  const { showToast } = useToast();

  const fetchCart = async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:8000/api/cart/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setCartData(data);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user && token) {
      fetchCart();
    } else {
      setCartData({ items: [], total_items: 0, total_price: 0 });
    }
  }, [user, token]);

  const addToCart = async (productId, quantity = 1) => {
    if (!token) {
      throw new Error('Please login to add items to cart');
    }

    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:8000/api/cart/add/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCartData(data.cart);
        return { success: true, message: data.message };
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add item to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`http://localhost:8000/api/cart/update/${itemId}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity }),
      });

      if (response.ok) {
        await fetchCart();
        return { success: true };
      }
    } catch (error) {
      console.error('Error updating cart item:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`http://localhost:8000/api/cart/remove/${itemId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await fetchCart();
        showToast('Item removed from cart', 'success');
        return { success: true };
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      showToast('Failed to remove item', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const clearCart = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await fetch('http://localhost:8000/api/cart/clear/', {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setCartData({ items: [], total_items: 0, total_price: 0 });
        showToast('Cart cleared successfully', 'success');
        return { success: true };
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      showToast('Failed to clear cart', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    cartData,
    cartItems: cartData.items,
    cartCount: cartData.total_items,
    cartTotal: cartData.total_price,
    isLoading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    fetchCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
